(ns ui.components.drawer.view
  "The Drawer of ZapKit, from the generated `ui.components.drawer.zapkit-styles`.
   It has two modes:

   - `:overlay` (the default): a panel that is fixed to the right of the window, over
     an overlay. It is mounted only when `:visible?`. The opportunity creator uses it.
   - `:inline`: a panel that stays in the layout of its parent and docks to the right,
     with no overlay. It takes the height of the parent and the width of `:size`. The
     record preview of the listings uses it. The caller handles Esc.

   Both modes have a header, a body and an optional footer."
  (:require
   [ui.components.drawer.zapkit-styles :as styles]
   [ui.components.zapkit-variants :as variants]
   [ui.util :as uu]))

(def dom-overrides
  "Classes that the generated styles do not have. `:close-button` keeps the close button
   from shrinking in the header row."
  {:close-button "tw2-flex-shrink-0"})

(defn panel-class
  "The classes of the panel. In the overlay mode the panel is fixed to the window. In the
   inline mode it stays in the layout. `size` is `:sm`, `:md`, `:lg` or `:full`. The overlay
   mode has no size unless the caller gives one, because its caller sets the width with
   `class`. The inline mode has the size `:md` (520 px) when the caller gives none."
  [{:keys [mode size class]}]
  (let [inline? (= :inline mode)]
    (uu/cm (variants/slot-class (:panel styles/drawer)
                                (cond-> {:placement (if inline?
                                                      :inline
                                                      :overlay-viewport)
                                         :open      true}
                                  (or size inline?) (assoc :size (or size :md))))
           class)))

(defn title-class
  "The classes of a title: the title of the Drawer. `class` is added last."
  [{:keys [class]}]
  (uu/cm (variants/slot-class (:title styles/drawer) {})
         class))

(defn title-view
  "A heading with the title classes of the Drawer. It is for the title of a record in the body of
   a Drawer whose caller draws its own header. `props` has `:class`, which is added last. The
   children are the text.
     [drawer/title-view {} \"Pharmia\"]"
  [props & children]
  (into [:h2 {:class (title-class props)}]
        children))

(defn overlay-class
  "The classes of the overlay of the overlay mode."
  [{:keys [class]}]
  (uu/cm (variants/slot-class (:overlay styles/drawer)
                              {:anchor  :viewport
                               :visible true})
         class))

(defn body-class
  "The classes of the body. `class` is added to them, for example `tw2-p-0` for a body whose
   content has its own padding."
  [{:keys [class]}]
  (uu/cm (variants/slot-class (:body styles/drawer) {})
         class))

(defn- header
  [{:keys [hide! close-button? header-child title]}]
  [:div
   {:class (variants/slot-class (:header styles/drawer) {})}
   (or header-child
       [:div
        {:class (variants/slot-class (:header-row styles/drawer) {})}
        [:div
         {:class (variants/slot-class (:title styles/drawer) {})}
         title]
        (when (and close-button? (fn? hide!))
          [:button
           {:class    (:close-button dom-overrides)
            :on-click hide!}
           [:i.fal.fa-times]])])])

(defn view
  "Render the drawer. Nothing is rendered when `:visible?` is not true.

   `:mode` is `:overlay` (the default) or `:inline`. `:size` is `:sm`, `:md`, `:lg` or
   `:full`, see `panel-class`. `:class` is added to the panel. `:body-class` is added to
   the body. `:overlay-class` is added to the overlay. `:hide!` is called by the close button and, in the overlay mode, by a
   click on the overlay (unless `:prevent-overlay-close?`). `:close-button?` shows the
   close button in the header. `:header-child` replaces the content of the header: the
   caller draws its own row. The inline panel is a `complementary` landmark, and `:aria-label`
   is its name.

   `:title`, `:body` and `:footer` are the hiccup of the three parts. The footer is optional:
     [drawer/view {:visible? true :title [:h2 \"Title\"] :body [:div \"Body\"]}]"
  [{:keys [visible? mode hide! prevent-overlay-close? aria-label body footer] :as props}]
  (when visible?
    (let [inline? (= :inline mode)
          panel [:div
                 (cond-> {:class (panel-class props)}
                   inline? (assoc :role "complementary")
                   (and inline? aria-label) (assoc :aria-label aria-label))
                 (header props)
                 [:div
                  {:class (body-class {:class (:body-class props)})}
                  body]
                 (when footer
                   [:div
                    {:class (variants/slot-class (:footer styles/drawer) {})}
                    footer])]]
      (if inline?
        panel
        [:div
         {:class "tw2"}
         [:div
          {:class    (overlay-class {:class (:overlay-class props)})
           :on-click (when (and (fn? hide!)
                                (not prevent-overlay-close?))
                       hide!)}]
         panel]))))
